<template>
  <section class="flex flex-col mt-6 mb-3">
    <span
      class="flex-grow flex flex-col sm:flex-row text-gray-600 items-center"
    >
      <span
        class="flex-grow flex flex-col sm:flex-row text-gray-600 items-center"
      >
        <span class="flex text-gray-600 items-center">
          <label for="transaction-start-date" class="w-full sm:w-auto pl-4"
            >Start Date</label
          >
          <input
            id="transaction-start-date"
            type="date"
            class="ml-2 flex-grow focus:outline-none placeholder-gray-400 sm:text-sm date_inp"
            v-model="payload.start_date"
            @change="changeFilter()"
          />
        </span>

        <span class="flex text-gray-600 items-center">
          <label for="transaction-end-date" class="w-full sm:w-auto pl-4"
            >End Date</label
          >
          <input
            id="transaction-end-date"
            type="date"
            class="ml-2 flex-grow focus:outline-none placeholder-gray-400 sm:text-sm date_inp"
            v-model="payload.end_date"
            @change="changeFilter()"
          />
        </span>

        <span class="flex text-gray-600 items-center">
          <label for="transaction-status" class="w-full sm:w-auto pl-4"
            >Status</label
          >
          <select
            id="transaction-status"
            class="form-field-no-icon sm:ml-2 w-full sm:w-40 mb-2 sm:mb-0 uppercase"
            v-model="payload.status"
            @change="changeFilter()"
          >
            <option value="">ALL</option>
            <option v-for="status in statuses" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
        </span>
      </span>
      <SearchInput
        v-model="payload.search"
        placeholder="Search by name, email or charge ID"
        aria-label="Search by name, email or charge ID"
        @input="changeFilter()"
      />
    </span>
    <span class="flex pl-4 mt-6 mb-6 text-gray-600 items-center">
      <p class="pr-5">TOTAL: {{ formatN(count) }}</p>
    </span>
  </section>
  <div class="align-middle p-5 bg-white inline-block w-full">
    <table class="divide-gray-300 w-full text-left table-auto">
      <thead class="border-b-2">
        <tr>
          <th
            v-for="columnName in columnNames"
            :key="columnName.name"
            scope="col"
            :class="`
                px-6
                py-5
                text-left text-xs
                font-medium
                text-gray-500
                uppercase
                tracking-wider
              `"
          >
            <FilterableHeaderCell
              v-if="columnName.param"
              :title="columnName.name"
              :param="columnName.param"
              :current-ordering="payload.ordering"
              @switchOrder="onSwitchOrders"
            />
            <span v-else>{{ columnName.name }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="data?.length">
          <tr v-for="row in data" :key="row.id">
            <td class="px-6 py-5 text-sm text-gray-500">
              {{ formatDate(row.created_at) }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              <router-link
                v-if="row.created_by"
                :to="{
                  name: 'UserDetails',
                  params: { userId: row.created_by.id },
                }"
                class="text-blue-600 hover:underline"
              >
                {{ row.created_by.email }}
              </router-link>
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              {{ formatN(row.credits) }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              {{ formatN(row.price) }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              {{ row.payment_method_display }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              {{ row.status }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500">
              <a
                v-if="row.stripe_dashboard_url"
                :href="row.stripe_dashboard_url"
                target="_blank"
                rel="noopener"
                class="text-blue-600 hover:underline"
              >
                View in Stripe
              </a>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td
              class="px-6 py-5 text-sm text-gray-500 text-left"
              :colspan="columnNames.length"
            >
              No data
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  <Paginator
    ref="paginator"
    :onPageChange="onPageChange"
    :pageSize="15"
    :data="paginationData"
  />
</template>

<script>
import apiMixin from "@/mixins/apiMixin";
import FilterableHeaderCell from "@/components/common/FilterableHeaderCell";
import Paginator from "@/components/common/Paginator";
import SearchInput from "@/components/common/SearchInput";
import humanize from "humanize";
import { mapGetters } from "vuex";

export default {
  name: "AdminTransactionHistory",
  mixins: [apiMixin],
  components: {
    Paginator,
    FilterableHeaderCell,
    SearchInput,
  },
  data() {
    return {
      data: [],
      statuses: ["INITIATED", "COMPLETED", "ERROR"],
      columnNames: [
        { name: "Date", param: "created_at" },
        { name: "User", param: "created_by__email" },
        { name: "Credits", param: "credits" },
        { name: "Price", param: "price" },
        { name: "Payment Method", param: "" },
        { name: "Status", param: "" },
        { name: "Stripe", param: "" },
      ],
      paginationData: "",
      payload: {
        start_date: "",
        end_date: "",
        status: "",
        page: 1,
        ordering: "-created_at",
        search: "",
      },
      count: "",
      requestSequence: 0,
    };
  },
  computed: {
    ...mapGetters({ adminStatsDate: "settings/adminStatsDate" }),
  },
  mounted() {
    this.payload.end_date = this.adminStatsDate.end_date;
    this.payload.start_date = this.adminStatsDate.start_date;
    this.fetchTransactions();
  },
  methods: {
    async fetchTransactions() {
      const requestId = ++this.requestSequence;
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () =>
          await this.$alfredService.AdminTransactions(this.payload),
      });
      if (response && requestId === this.requestSequence) {
        this.data = response.results;
        this.count = response.count;
        this.paginationData = response;
      }
    },
    async changeFilter() {
      const admin_stats_date = {
        start_date: this.payload.start_date,
        end_date: this.payload.end_date,
      };
      this.$emit("getDate", admin_stats_date);
      this.$store.dispatch("settings/SET_ADMIN_STATS_DATE", admin_stats_date);
      await this.$refs.paginator.setPage(1);
    },
    async onPageChange(page) {
      this.payload.page = page;
      await this.fetchTransactions();
    },
    async onSwitchOrders(ordering) {
      this.payload.ordering = ordering;
      await this.$refs.paginator.setPage(1);
    },
    formatN(count) {
      const num = parseFloat(count);
      return humanize.numberFormat(num, num % 1 === 0 ? 0 : 2);
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString("en-US");
    },
  },
};
</script>
<style scoped>
select {
  background-image: none;
  border-color: #ffffff00;
}

.date_inp {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-width: 1px;
  border-radius: 0.375rem;
  --tw-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}
</style>
